import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { observer } from 'mobx-react'
import Loader from 'components/UI/Loader/Loader'
import Exercise from '../Common/Exercise/Exercise'
import { LessonContext } from '../BookChapter/LessonContext'
import BookPaper from '../Common/BookPaper/BookPaper'
import useLocalization from 'hooks/useLocalization'
import WithoutExercises from '../Common/WithoutExercises/WithoutExercises'
import st from '../LessonTeacher/LessonTeacher.module.scss'
import LessonStudentStore from '../LessonStudent/lessonStudentStore'
import LessonToolbar from '../LessonStudent/LessonToolbar'
import LessonHomeworkHeader from '../Common/LessonHomeworkHeader/LessonHomeworkHeader'
import StudentSelector from '../HomeworkTeacher/StudentSelector'
import LessonLogs from '../LessonLogs/LessonLogs'
import ActionWithEx from '../ActionWithEx/ActionWithEx'
import swal from 'sweetalert'
import { localisationMaster } from '../../../../Localisation/Localisation'
import { superAxiosRequest } from '../../../../axiosApi'
import CreateDiscussionPastLesson from '../LessonDiscussion/CreateDiscussionPastLesson/CreateDiscussionPastLesson'
import ShowMark from '../ShowMark/ShowMark'

const PastLessonTeacher = () => {
  const { uuid } = useParams()

  const [currentRef, setCurrentRef] = useState(null)

  const [mark, setMark] = useState(null)
  const [studentMark, setStudentMark] = useState(null)
  const [review, setReview] = useState(null)
  const [isSubmit, setIsSubmit] = useState(false)

  const { lesson, fetchPastLesson, errorStatus } = LessonStudentStore

  const context = { mode: 'teacher-homework' }

  const scrollToBlock = () => {
    if (currentRef.current) {
      currentRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const [selectedStudent, setSelectedStudent] = useState(null)

  const { translate } = useLocalization({
    section: 'ebooks',
    subsection: 'exercises',
    dict: {
      provide_exercise: 'Provide An Exercise',
      show_answers: 'Show Answers',
      focus: 'Focus On Exercise',
      add_to_homework: 'Add to Homework',
      select_student: 'Select Student',
      past_lesson: 'Past Lesson',
      homework: 'Homework',
      exit: 'Exit',
      no_exercises: 'No exercises',
      no_ex_descr:
        'During the lesson you did not provide any exercises to the students',
      action_with_homework: 'Action With Homework',
      submit_hm: 'Submit Homework For Revision',
      or_rate: 'OR RATE',
      confirm_the_lesson: 'Confirm Homework',
      homework_failed: 'Homework Failed',
      action_canceled: 'Action canceled',
      homework_passed: 'Homework Passed',
      teacher_confirmed_the_lesson: 'Teacher Confirmed The Lesson',
      failed: 'Failed',
      alert_submit_hm:
        'Are you sure you want to submit your homework for revision',
      discussion: 'Discussion',
      homework_checking: 'Homework has been sent to the teacher for checking',
      homework_is_checked: 'Homework is checked by the teacher',
      sent_for_revision: 'and sent for revision',
      Homework_has_been_checked: 'Homework has been checked by the teacher',
      and_confirmed: 'and confirmed',
      error: 'Error',
      didnt_answer_task: 'You didn’t answer all the tasks in the exercise',
      save_answer: 'Save Answer',
      add_to_favorites: 'Add to Favorites',
      dislike: 'Dislike',
      dont_forget: "Don't forget to save your answer",
    },
  })

  const sendToRevision = () => {
    swal({
      text: `${translate.alert_submit_hm}?`,
      icon: 'warning',
      buttons: [
        localisationMaster('button_2', 'alert'),
        localisationMaster('button_1', 'alert'),
      ],
      dangerMode: true,
    }).then((yes) => {
      if (yes) {
        superAxiosRequest(
          {
            method: 'post',
            link: 'ebooks/stud_les_grades',
          },
          {
            student_id: selectedStudent?.id,
            uuid: uuid,
          },
        ).then(() => setIsSubmit(true))
      } else {
        swal(translate.action_canceled)
      }
    })
  }
  const addMark = (mark) => {
    superAxiosRequest(
      {
        method: 'post',
        link: 'ebooks/student_lesson_marks',
      },
      {
        uuid: uuid,
        student_id: selectedStudent.id,
        mark: mark,
      },
    ).then((req) => {
      setMark(req.data?.mark)
      setIsSubmit(true)
      fetchPastLesson(uuid)
    })
  }

  useEffect(() => LessonStudentStore.clearLesson(), [])

  useEffect(() => {
    fetchPastLesson(uuid)
  }, [uuid, fetchPastLesson])

  useEffect(() => {
    setStudentMark(
      lesson?.students_marks?.find(
        (el) => el.student_id === selectedStudent?.id,
      ),
    )
    setReview(
      lesson?.students_reviews?.find(
        (el) => el.student_id === selectedStudent?.id,
      ),
    )
  }, [lesson, selectedStudent, studentMark])

  return lesson ? (
    <>
      <LessonToolbar
        homeworkLink={
          lesson.homework ? `/ebooks/homework/${lesson.homework}` : ''
        }
      />
      <div className={st.page}>
        <div className={st.container}>
          <BookPaper>
            <div className={st.bookColumn}>
              <LessonHomeworkHeader
                title={translate.past_lesson}
                createdAt={lesson.created_at}
                ebookName={lesson.ebook_name}
                chapterName={lesson.chapter_name}
              />
              <StudentSelector
                students={lesson.students}
                student={selectedStudent}
                onChange={setSelectedStudent}
                translate={translate}
              />
              {!!studentMark?.mark && (
                <ShowMark
                  translate={translate}
                  mark={Number(studentMark?.mark)}
                  type={lesson?.company_mark_type}
                />
              )}
              {lesson.exercises.length > 0 ? (
                <LessonContext.Provider value={context}>
                  <div className={st.questions}>
                    {lesson.exercises.map((ex, index) => (
                      <Exercise
                        key={ex.id}
                        number={`1.${index + 1}`}
                        ex={ex}
                        student={selectedStudent}
                        answers={
                          lesson.assign_exercises
                            .find((ae) => ae.exercise_id === ex.id)
                            ?.students.find(
                              (st) => st.student_id === selectedStudent?.id,
                            )?.answers || []
                        }
                        translate={translate}
                      />
                    ))}
                  </div>
                </LessonContext.Provider>
              ) : (
                <WithoutExercises
                  text1={`${translate.no_exercises}`}
                  text2={`${translate.no_ex_descr}.`}
                />
              )}
            </div>
          </BookPaper>
          {lesson.company_marking && (
            <>
              {review?.review && !studentMark?.mark && !isSubmit && (
                <ActionWithEx
                  translate={translate}
                  setMark={setMark}
                  pastLesson={lesson}
                  addMark={addMark}
                  sendToRevision={sendToRevision}
                />
              )}
              {selectedStudent && (
                <CreateDiscussionPastLesson
                  isHomework={false}
                  context={'teacher'}
                  studentId={selectedStudent?.id}
                  setRef={setCurrentRef}
                />
              )}
              {selectedStudent && (
                <LessonLogs
                  translate={translate}
                  context={'teacher'}
                  isHomeWork={false}
                  studentId={selectedStudent?.id}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  ) : (
    <div className={st.page}>
      <div className={st.container}>
        <BookPaper>
          {errorStatus === 404 ? (
            <WithoutExercises text1='404' text2='Lesson not found' />
          ) : (
            <Loader />
          )}
        </BookPaper>
      </div>
    </div>
  )
}

export default observer(PastLessonTeacher)
